<template>
  <v-navigation-drawer
    :value="value"
    fixed
    right
    width="400"
    @input="$emit('input', $event)"
  >
    <v-card flat>
      <v-toolbar class="mb-3">
        <v-btn
          v-if="forgotPasswordLayout"
          :disabled="loading.login"
          icon
          @click="forgotPasswordLayout = false"
        >
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-btn
          v-else
          icon
          @click="closeDrawer"
        >
          <v-icon>close</v-icon>
        </v-btn>
        <v-card-title>
          {{ forgotPasswordLayout ? 'Reset password' : 'Log in child profile' }}
        </v-card-title>
      </v-toolbar>

      <ResetPasswordDrawerForm v-show="forgotPasswordLayout" />
      <div v-show="!forgotPasswordLayout">
        <v-form
          v-model="validForm"
          :disabled="loading.login"
          @submit.prevent="onLogin"
        >
          <v-card-text>
            <v-text-field
              v-model="loginFormData.profileId"
              :type="profileIdInputType"
              :rules="rules.profileId"
              label="Email or profile id"
              outlined
            />
            <v-text-field
              ref="passwordField"
              v-model="loginFormData.password"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'visibility' : 'visibility_off'"
              :rules="rules.password"
              :hide-details="$refs.passwordField && $refs.passwordField.valid"
              label="Password"
              outlined
              @click:append="togglePasswordVisibility"
            />
            <v-checkbox
              v-model="loginFormData.rememberMe"
              label="Remember user account"
              hide-details
            />
          </v-card-text>
          <v-card-actions>
            <v-btn
              :disabled="!validForm"
              :loading="loading.login"
              type="submit"
              block
              color="blue"
              class="white--text"
            >
              Login
            </v-btn>
          </v-card-actions>
        </v-form>

        <v-divider class="my-3" />

        <v-card-actions class="py-0">
          <v-btn
            :disabled="loading.login"
            text
            small
            color="blue"
            class="mx-auto"
            @click="forgotPasswordLayout = true"
          >
            Forgot your password?
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapActions } from 'vuex';
import { profileIdRegex, emailRegex, passwordRegex } from '@/helpers/regex';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

function useLoginFormData() {
  return {
    profileId: '',
    password: '',
    rememberMe: true,
  };
}

export default {
  name: 'LogInNavigationDrawer',
  components: {
    ResetPasswordDrawerForm: () => import('./ResetPasswordDrawerForm'),
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      forgotPasswordLayout: false,
      validForm: false,
      loginFormData: useLoginFormData(),
      rules: {
        profileId: [
          v => !!v || 'Required field',
          v => profileIdRegex.test(v) || emailRegex.test(v) || 'Invalid format',
        ],
        password: [
          v => !!v || 'Required field',
          v => v.length >= 8 || 'Min length: 8',
          v => passwordRegex.test(v)
            || 'Password must be at least 8 characters long and contain at least one lowercase letter, uppercase letter, number and symbol',
        ],
      },
      showPassword: false,
      loading: {
        login: false,
      },
    };
  },
  computed: {
    profileIdInputType() {
      return this.loginFormData.profileId.includes('@') ? 'email' : 'text';
    },
  },
  methods: {
    ...mapActions('children', [
      'loginChild',
    ]),
    onLogin() {
      if (this.loading.login) return;

      this.loading.login = true;

      const credentials = cloneDeep(this.loginFormData);

      this.loginChild(credentials)
        .then(() => {
          this.$toast.success('Logged in successfully');
          this.closeDrawer();
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to login'))
        .finally(() => (this.loading.login = false));
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    closeDrawer() {
      this.$emit('input', false);
    },
  },
};
</script>
